import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Hidden from "@material-ui/core/Hidden";
import MoreVert from "@material-ui/icons/MoreVert";
import { getIn } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { KeyboardArrowUp, KeyboardArrowDown } from "@material-ui/icons";
import { Grid, IconButton } from "@material-ui/core";
import { compareSorting } from "./utils";
import clsx from "clsx";
const useStyles = makeStyles(theme => ({
    root: {},
    expandedColumn: {
        position: "relative"
    },
    editColumnCommon: {
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        "&:last-child": {
            padding: 0
        }
    },
    editColumnHeaderCell: (props) => ({
        cursor: props.onRowClick ? "pointer" : "auto"
    }),
    editColumnRowCell: (props) => ({
        "&:hover": {
            cursor: "pointer",
            background: props.onRowClick
                ? theme.palette.grey[300]
                : theme.palette.grey[200]
        }
    }),
    emptyLastColumn: {
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        "&:last-child": {
            padding: 0
        }
    },
    editIcon: {
        width: "100%"
    },
    clickableRow: {
        "&:hover": {
            cursor: "pointer",
            background: theme.palette.grey[200]
        }
    },
    head: {},
    columnLabel: {
        alignSelf: "center",
        paddingRight: 8
    },
    sortArrowsContainer: {
        height: 20,
        width: 20
    },
    headerGridObject: {},
    expandedRow: {}
}), { name: "DployExpandableTable" });
export var ColumnSortOrderEnum;
(function (ColumnSortOrderEnum) {
    ColumnSortOrderEnum[ColumnSortOrderEnum["Disabled"] = -1] = "Disabled";
    ColumnSortOrderEnum[ColumnSortOrderEnum["NotSet"] = 0] = "NotSet";
    ColumnSortOrderEnum[ColumnSortOrderEnum["Asc"] = 1] = "Asc";
    ColumnSortOrderEnum[ColumnSortOrderEnum["Desc"] = 2] = "Desc";
})(ColumnSortOrderEnum || (ColumnSortOrderEnum = {}));
export const ExpandableTable = (props) => {
    const { tableData, columnData, idColumn = "id", onRowClick, renderExpandedSection, tableType, expandable = true, size, tableIsSortable, pagination } = props;
    const [expanded, setExpanded] = useState({});
    const classes = useStyles(props);
    const onMoreInfoClick = (row) => (event) => {
        event.stopPropagation();
        return setExpanded(state => {
            const id = row[idColumn];
            if (state[id]) {
                const { [id]: _, ...expanded } = state;
                return expanded;
            }
            else {
                return {
                    ...state,
                    [id]: true
                };
            }
        });
    };
    const [filteredColumns, setFilteredColumns] = useState(columnData);
    const [orderedRows, setOrderedRows] = useState(tableData);
    const paginationPage = pagination?.page;
    const paginationRowsPerPage = pagination?.rowsPerPage;
    const visibleRows = useMemo(() => {
        if (paginationRowsPerPage === undefined ||
            paginationRowsPerPage <= 0 ||
            orderedRows.length === 0) {
            return orderedRows;
        }
        const safePage = Math.max(paginationPage ?? 0, 0);
        const startIndex = safePage * paginationRowsPerPage;
        return orderedRows.slice(startIndex, startIndex + paginationRowsPerPage);
    }, [orderedRows, paginationPage, paginationRowsPerPage]);
    useEffect(() => {
        const columnsWithSorting = filteredColumns.filter(x => (x.sortOrder ?? 0) > 0);
        const columnToSortBy = columnsWithSorting[0];
        if (columnsWithSorting.length > 1) {
            console.warn(`There are more than one columns in ExpandableTable that are marked as sorting columns. Using the first column in table as sorting column (${columnToSortBy})`);
        }
        if (tableIsSortable && columnToSortBy) {
            var copiedTabledDataToMutate = Object.assign([], tableData);
            setOrderedRows(copiedTabledDataToMutate.sort((a, b) => {
                return compareSorting(a[columnToSortBy.id ?? ""], b[columnToSortBy.id ?? ""], columnToSortBy.sortOrder);
            }));
        }
        else {
            //	If no column is marked as sorting, use default tableData order from props
            setOrderedRows(tableData);
        }
    }, [filteredColumns, tableIsSortable, tableData]);
    useEffect(() => {
        setFilteredColumns(columnData
            .filter(col => col.hidden !== true && col.id)
            .map(col => {
            return {
                ...col,
                sortOrder: col.sortOrder ?? ColumnSortOrderEnum.NotSet
            };
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnData]);
    // NotSet -> Asc -> Desc -> NotSet -> ...
    const calculateLoopingSortOrder = useMemo(() => (sortOrder) => {
        let sum = sortOrder + 1;
        if (sum > 2)
            sum = 0;
        else if (sum < 0)
            sum = 2;
        return sum;
    }, []);
    const handleSortIconClick = useCallback((selectedColumn) => {
        setFilteredColumns(filteredColumns.map(x => {
            return {
                ...x,
                sortOrder: x.id === selectedColumn.id
                    ? calculateLoopingSortOrder(x.sortOrder)
                    : (x.sortOrder ?? 0) > 0
                        ? ColumnSortOrderEnum.NotSet
                        : x.sortOrder
            };
        }));
    }, [calculateLoopingSortOrder, filteredColumns]);
    return (_jsxs(Table, { size: size, className: classes.root, "data-tabletype": tableType, children: [_jsx(Hidden, { xsDown: true, children: _jsx(TableHead, { children: _jsxs(TableRow, { children: [filteredColumns.map((col, i) => (_jsx(Hidden, { ...(typeof col.hidden === "object" ? col.hidden : null), children: _jsx(TableCell, { classes: { head: classes.head }, children: _jsxs(Grid, { container: true, direction: "row", className: classes.headerGridObject, children: [_jsx(Grid, { item: true, className: classes.columnLabel, children: col.label }), tableIsSortable && (col.sortOrder ?? 0) > -1 && (_jsx(Grid, { item: true, container: true, direction: "column", style: { width: "min-content" }, children: col.sortOrder === ColumnSortOrderEnum.NotSet ? (_jsxs(_Fragment, { children: [_jsx(ColumnSortArrow, { className: classes.sortArrowsContainer, upArrow: true, onClick: handleSortIconClick, column: col }), _jsx(ColumnSortArrow, { className: classes.sortArrowsContainer, onClick: handleSortIconClick, column: col })] })) : col.sortOrder === ColumnSortOrderEnum.Asc ? (_jsx(ColumnSortArrow, { className: classes.sortArrowsContainer, onClick: handleSortIconClick, column: col })) : col.sortOrder === ColumnSortOrderEnum.Desc ? (_jsx(ColumnSortArrow, { className: classes.sortArrowsContainer, upArrow: true, onClick: handleSortIconClick, column: col })) : null }))] }) }) }, i))), expandable && (_jsx(TableCell, { className: clsx(classes.editColumnCommon, classes.editColumnHeaderCell) }))] }) }) }), _jsx(TableBody, { children: visibleRows &&
                    visibleRows.map(row => (_jsxs(React.Fragment, { children: [_jsxs(TableRow, { onClick: onRowClick ? () => onRowClick(row) : undefined, className: clsx(onRowClick ? classes.clickableRow : undefined, expanded[row[idColumn]] ? classes.expandedRow : undefined), children: [filteredColumns.map(col => {
                                        const CellComponent = col.component || DefaultCell;
                                        const cellData = getIn(row, col.id);
                                        const value = col.format
                                            ? col.format(cellData, row, undefined)
                                            : cellData;
                                        return (_jsx(Hidden, { ...(typeof col.hidden === "object" ? col.hidden : null), children: _jsx(TableCell, { className: col.className, children: CellComponent ? (_jsx(CellComponent, { rowData: row, ...col.props, children: value })) : (value) }) }, col.id?.toString() ?? col.loadedId?.toString()));
                                    }), expandable && (_jsx(TableCell, { className: clsx(classes.editColumnCommon, classes.editColumnRowCell), onClick: onMoreInfoClick(row), children: _jsx(MoreVert, { className: classes.editIcon }) }))] }), expanded[row[idColumn]] ? (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: columnData.length + 1, className: classes.expandedColumn, children: renderExpandedSection && renderExpandedSection(row) }) })) : null] }, row[idColumn]))) })] }));
};
const DefaultCell = ({ children }) => _jsx("span", { children: children });
const ColumnSortArrow = (props) => {
    const { upArrow, className, onClick, column } = props;
    return (_jsx(Grid, { item: true, className: className, children: _jsx(IconButton, { className: className, onClick: () => onClick(column), children: upArrow ? _jsx(KeyboardArrowUp, {}) : _jsx(KeyboardArrowDown, {}) }) }));
};
//# sourceMappingURL=ExpandableTable.js.map